import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Check, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { votePoll } from "@/lib/api";

export default function PollView({ threadId, poll: initial }) {
  const { pick } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [poll, setPoll] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(poll?.my_votes || []);

  if (!poll) return null;
  const hasVoted = (poll.my_votes || []).length > 0;
  const total = poll.total_votes || 0;

  const toggle = (id) => {
    if (poll.multi) setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    else setSelected([id]);
  };

  const submit = async () => {
    if (!user) return navigate("/login");
    if (!selected.length) return;
    setBusy(true);
    try {
      const updated = await votePoll(threadId, selected);
      setPoll(updated);
      setSelected(updated.my_votes || []);
    } finally { setBusy(false); }
  };

  const showResults = hasVoted;

  return (
    <div className="mt-4 border border-line bg-surface p-4" data-testid="poll-view">
      <p className="mb-3 inline-flex items-center gap-1.5 font-serif-latin text-base text-ink">
        <BarChart3 className="h-4 w-4 text-gold" /> {poll.question || pick("मतदान", "Poll")}
      </p>
      <div className="space-y-2">
        {poll.options.map((o) => {
          const pct = total ? Math.round((o.votes / total) * 100) : 0;
          const mine = (poll.my_votes || []).includes(o.id);
          if (showResults) {
            return (
              <div key={o.id} data-testid={`poll-result-${o.id}`} className="relative overflow-hidden rounded border border-line">
                <div className="absolute inset-0 bg-saffron/10" style={{ width: `${pct}%` }} />
                <div className="relative flex items-center justify-between px-3 py-2 text-sm">
                  <span className={`inline-flex items-center gap-1.5 ${mine ? "font-medium text-saffron" : "text-ink"}`}>
                    {mine && <Check className="h-3.5 w-3.5" />} {o.text}
                  </span>
                  <span className="text-xs text-ink-3">{pct}% · {o.votes}</span>
                </div>
              </div>
            );
          }
          return (
            <button key={o.id} type="button" onClick={() => toggle(o.id)} data-testid={`poll-choose-${o.id}`}
              className={`flex w-full items-center gap-2 rounded border px-3 py-2 text-left text-sm transition-colors ${selected.includes(o.id) ? "border-saffron text-saffron" : "border-line text-ink hover:border-saffron/60"}`}>
              <span className={`flex h-4 w-4 items-center justify-center border ${poll.multi ? "rounded-sm" : "rounded-full"} ${selected.includes(o.id) ? "border-saffron bg-saffron text-white" : "border-ink-3"}`}>
                {selected.includes(o.id) && <Check className="h-3 w-3" />}
              </span>
              {o.text}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-ink-3">
        <span>{total} {pick("मत", total === 1 ? "vote" : "votes")}</span>
        {!showResults && (
          <button onClick={submit} disabled={busy || !selected.length} data-testid="poll-submit"
            className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {pick("मत दें", "Vote")}
          </button>
        )}
      </div>
    </div>
  );
}
